import React, { useCallback, useMemo, useState } from "react";
import { Plus, GripVertical, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LIMITS, LAYOUT_COMPONENT_TYPES, LAYOUT_TEXT_TYPES } from "../../utils/flowConstants";
import { createComponent, uid } from "../../utils/flowJsonBuilder";
import FormEditor from "./FormEditor";
import { uploadFlowImage } from "../../apis/whatsappFlows";
import { getFooterAutoPayload, getScreenAutoTokens } from "../../utils/flowAutoMapping";

const SortableLayoutItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const TEXT_CARD_STYLES = {
  TextHeading: { label: "Text Heading", border: "border-gray-200", bg: "bg-white", text: "text-gray-600" },
  TextSubheading: { label: "Text Subheading", border: "border-indigo-200", bg: "bg-indigo-50/30", text: "text-indigo-600" },
  TextBody: { label: "Text Body", border: "border-sky-200", bg: "bg-sky-50/30", text: "text-sky-600" },
  TextCaption: { label: "Text Caption", border: "border-amber-200", bg: "bg-amber-50/30", text: "text-amber-600" },
  RichText: { label: "Rich Text", border: "border-violet-200", bg: "bg-violet-50/30", text: "text-violet-600" },
};

const ScreenEditor = ({ screen, screens, onUpdate }) => {
  const layoutChildren = useMemo(() => screen.layout?.children || [], [screen.layout]);
  const [imageUploadingIdx, setImageUploadingIdx] = useState(null);
  const autoPayload = getFooterAutoPayload({ screens }, screen.id);
  const tokenHints = getScreenAutoTokens({ screens }, screen.id);

  const handleLayoutImageUpload = useCallback(
    async (idx, event) => {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be 5MB or smaller.");
        event.target.value = "";
        return;
      }

      setImageUploadingIdx(idx);
      try {
        const url = await uploadFlowImage(file);
        const target = layoutChildren[idx];
        if (!target || target.type !== "Image") return;
        const updated = { ...target, src: url };
        const newChildren = layoutChildren.map((c, i) => (i === idx ? updated : c));
        onUpdate({ ...screen, layout: { ...screen.layout, children: newChildren } });
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Image upload failed.";
        toast.error(message);
      } finally {
        setImageUploadingIdx(null);
        event.target.value = "";
      }
    },
    [layoutChildren, onUpdate, screen]
  );

  const hasRichTextOnScreen = useMemo(
    () =>
      layoutChildren.some((child) => {
        if (child.type === "RichText") return true;
        if (child.type === "Form") {
          return (child.children || []).some((fc) => fc.type === "RichText");
        }
        return false;
      }),
    [layoutChildren]
  );

  const hasAnyNonRichComponentOnScreen = useMemo(() => {
    let found = false;
    layoutChildren.forEach((child) => {
      if (child.type !== "RichText") found = true;
      if (child.type === "Form") {
        (child.children || []).forEach((fc) => {
          if (fc.type !== "RichText") found = true;
        });
      }
    });
    return found;
  }, [layoutChildren]);

  const updateLayoutChild = useCallback(
    (idx, updated) => {
      const newChildren = layoutChildren.map((c, i) => (i === idx ? updated : c));
      onUpdate({ ...screen, layout: { ...screen.layout, children: newChildren } });
    },
    [screen, layoutChildren, onUpdate]
  );

  const addLayoutComponent = useCallback(
    (type) => {
      if (type === "RichText") {
        if (hasRichTextOnScreen || hasAnyNonRichComponentOnScreen) {
          toast.error("RichText screen can only contain RichText and nothing else. Remove other components first.");
          return;
        }
      } else {
        if (hasRichTextOnScreen) {
          toast.error("Cannot add this component because RichText is present on this screen. Remove RichText or use another screen.");
          return;
        }
      }
      const comp = createComponent(type);
      onUpdate({ ...screen, layout: { ...screen.layout, children: [...layoutChildren, comp] } });
    },
    [screen, layoutChildren, onUpdate, hasRichTextOnScreen, hasAnyNonRichComponentOnScreen]
  );

  const addForm = useCallback(() => {
    if (hasRichTextOnScreen) {
      toast.error("Cannot add a Form when RichText is present on this screen. Remove RichText or use another screen.");
      return;
    }
    if (layoutChildren.some((c) => c.type === "Form")) return;
    const form = { _uid: uid(), type: "Form", name: `${screen.id.toLowerCase()}_form`, children: [] };
    onUpdate({ ...screen, layout: { ...screen.layout, children: [...layoutChildren, form] } });
  }, [screen, layoutChildren, onUpdate, hasRichTextOnScreen]);

  const hasFooterOutsideForm = useMemo(
    () => layoutChildren.some((c) => c.type === "Footer"),
    [layoutChildren]
  );

  const hasFooterInsideForm = useMemo(() => {
    const form = layoutChildren.find((c) => c.type === "Form");
    const children = form?.children || [];
    return children.some((c) => c.type === "Footer");
  }, [layoutChildren]);

  const addFooter = useCallback(() => {
    if (hasRichTextOnScreen) {
      toast.error("Cannot add a Footer when RichText is present on this screen. Remove RichText or use another screen.");
      return;
    }
    if (hasFooterInsideForm) {
      toast.error("Footer already exists inside the Form. Remove it before adding a footer outside the form.");
      return;
    }
    if (hasFooterOutsideForm) {
      toast.error("Only one Footer is allowed on a screen. Remove the existing footer first.");
      return;
    }
    const comp = createComponent("Footer");
    if (screen.terminal) comp["on-click-action"] = { name: "complete" };
    onUpdate({ ...screen, layout: { ...screen.layout, children: [...layoutChildren, comp] } });
  }, [screen, layoutChildren, onUpdate, hasFooterInsideForm, hasFooterOutsideForm, hasRichTextOnScreen]);

  const removeLayoutChild = useCallback(
    (idx) => {
      onUpdate({ ...screen, layout: { ...screen.layout, children: layoutChildren.filter((_, i) => i !== idx) } });
    },
    [screen, layoutChildren, onUpdate]
  );

  const hasForm = layoutChildren.some((c) => c.type === "Form");
  const richOnly = hasRichTextOnScreen;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const layoutSortableIds = useMemo(
    () => layoutChildren.map((c) => c._uid || `lc_${layoutChildren.indexOf(c)}`),
    [layoutChildren]
  );

  const handleLayoutDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = layoutSortableIds.indexOf(active.id);
      const newIdx = layoutSortableIds.indexOf(over.id);
      if (oldIdx !== -1 && newIdx !== -1) {
        onUpdate({
          ...screen,
          layout: { ...screen.layout, children: arrayMove(layoutChildren, oldIdx, newIdx) },
        });
      }
    },
    [screen, layoutChildren, layoutSortableIds, onUpdate]
  );

  const renderTextCard = (child, idx, style, dragHandleProps) => (
    <div className={`waf-block ${style.border} ${style.bg}`}>
      <div className="waf-block-head">
        <div className="flex items-center gap-1.5">
          <button
            {...(dragHandleProps || {})}
            className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing rounded"
            tabIndex={-1}
          >
            <GripVertical size={11} />
          </button>
          <span className={`text-[10px] font-semibold uppercase ${style.text}`}>{style.label}</span>
        </div>
        <button type="button" onClick={() => removeLayoutChild(idx)} className="waf-remove">Remove</button>
      </div>
      {child.type === "Image" ? (
        <div className="space-y-2">
          <input
            value={child.src || ""}
            onChange={(e) => updateLayoutChild(idx, { ...child, src: e.target.value })}
            className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40"
            placeholder="https://example.com/image.png"
          />
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="text-[10px] text-gray-500 file:mr-2 file:px-2 file:py-1 file:text-[10px] file:font-medium file:bg-white file:border file:border-gray-200 file:rounded-lg hover:file:bg-gray-50"
              onChange={(e) => handleLayoutImageUpload(idx, e)}
              disabled={imageUploadingIdx === idx}
            />
            {imageUploadingIdx === idx && (
              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                <Loader2 size={11} className="animate-spin" />
                Uploading…
              </span>
            )}
          </div>
          <input
            type="number"
            value={child.height || ""}
            onChange={(e) => updateLayoutChild(idx, { ...child, height: Number(e.target.value) || undefined })}
            className="w-32 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40"
            placeholder="Height (px)"
            min={LIMITS.IMAGE_HEIGHT_MIN}
            max={LIMITS.IMAGE_HEIGHT_MAX}
          />
          <p className="text-[10px] text-gray-400">Max 5MB • JPG, JPEG, PNG, WEBP</p>
        </div>
      ) : (
        <textarea
          value={child.text || ""}
          onChange={(e) => updateLayoutChild(idx, { ...child, text: e.target.value })}
          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 resize-none"
          placeholder={`${style.label} text`}
          rows={child.type === "RichText" || child.type === "TextBody" ? 3 : 1}
        />
      )}
    </div>
  );

  return (
    <div className="waf-screen-ed">
      <div className="waf-meta">
        <label>
          Screen ID
          <code>{screen.id}</code>
        </label>
        <label>
          Title
          <input
            value={screen.title || ""}
            onChange={(e) => onUpdate({ ...screen, title: e.target.value })}
            maxLength={LIMITS.SCREEN_TITLE_MAX_LEN}
          />
        </label>
        <label className="waf-check">
          <input
            type="checkbox"
            checked={screen.terminal || false}
            onChange={(e) => onUpdate({ ...screen, terminal: e.target.checked })}
          />
          Final screen
        </label>
      </div>

      <div className="waf-map">
        <span>Variables</span>
        {tokenHints.length > 0 ? (
          tokenHints.slice(0, 8).map((t) => <em key={t}>{t}</em>)
        ) : (
          <i>None yet</i>
        )}
        {tokenHints.length > 8 && <em>+{tokenHints.length - 8}</em>}
      </div>

      <div className="waf-add">
        {richOnly ? (
          <p className="waf-add-note">RichText-only screen. Remove RichText to add other components.</p>
        ) : (
          <>
            {LAYOUT_COMPONENT_TYPES.filter((lc) =>
              lc.value === "RichText" ? !hasAnyNonRichComponentOnScreen : true
            ).map((lc) => (
              <button key={lc.value} type="button" onClick={() => addLayoutComponent(lc.value)}>
                <Plus size={10} /> {lc.label}
              </button>
            ))}
            {!hasForm && (
              <button type="button" className="hi" onClick={addForm}>
                <Plus size={10} /> Form
              </button>
            )}
            <button
              type="button"
              className="hi"
              onClick={addFooter}
              disabled={hasFooterInsideForm || hasFooterOutsideForm}
            >
              <Plus size={10} /> Footer
            </button>
          </>
        )}
      </div>

        <div className="waf-blocks">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLayoutDragEnd}>
          <SortableContext items={layoutSortableIds} strategy={verticalListSortingStrategy}>
            {layoutChildren.map((child, idx) => (
              <SortableLayoutItem key={child._uid || idx} id={child._uid || `lc_${idx}`}>
                {({ dragHandleProps }) => (
                  <div>
                    {/* Text-like layout children + Image */}
                    {(LAYOUT_TEXT_TYPES.includes(child.type)) &&
                      renderTextCard(child, idx, TEXT_CARD_STYLES[child.type] || {
                        label: child.type, border: "border-gray-200", bg: "bg-white", text: "text-gray-500",
                      }, dragHandleProps)
                    }

                    {/* Form */}
                    {child.type === "Form" && (
                      <div className="waf-block form">
                        <div className="waf-block-head">
                          <div className="flex items-center gap-1.5">
                            <button
                              {...(dragHandleProps || {})}
                              className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing rounded"
                              tabIndex={-1}
                            >
                              <GripVertical size={11} />
                            </button>
                            <span className="text-[10px] font-bold uppercase">Form</span>
                          </div>
                          <button type="button" onClick={() => removeLayoutChild(idx)} className="waf-remove">Remove</button>
                        </div>
                        <FormEditor form={child} screens={screens} currentScreenId={screen.id} onUpdate={(updated) => updateLayoutChild(idx, updated)} />
                      </div>
                    )}

                    {child.type === "Footer" && (
                      <div className="waf-block foot">
                        <div className="waf-block-head">
                          <div className="flex items-center gap-1.5">
                            <button
                              {...(dragHandleProps || {})}
                              className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing rounded"
                              tabIndex={-1}
                            >
                              <GripVertical size={11} />
                            </button>
                            <span className="text-[10px] font-semibold text-teal-600 uppercase">Footer (outside form)</span>
                          </div>
                          <button type="button" onClick={() => removeLayoutChild(idx)} className="waf-remove">Remove</button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-gray-500 font-semibold">Label</label>
                            <input
                              value={child.label || ""}
                              onChange={(e) => updateLayoutChild(idx, { ...child, label: e.target.value })}
                              className="mt-0.5 w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40"
                              maxLength={LIMITS.FOOTER_LABEL_MAX}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 font-semibold">Action</label>
                            <select
                              value={child["on-click-action"]?.name || "navigate"}
                              onChange={(e) => {
                                const name = e.target.value;
                                updateLayoutChild(idx, {
                                  ...child,
                                  "on-click-action": name === "complete"
                                    ? { name: "complete" }
                                    : { name: "navigate", next: { type: "screen", name: "" } },
                                });
                              }}
                              className="mt-0.5 w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white"
                            >
                              <option value="navigate">Navigate</option>
                              <option value="complete">Complete</option>
                            </select>
                          </div>
                        </div>
                        {child["on-click-action"]?.name === "navigate" && (
                          <div className="mt-2">
                            <label className="text-[10px] text-gray-500 font-semibold">Target Screen</label>
                            <select
                              value={child["on-click-action"]?.next?.name || ""}
                              onChange={(e) =>
                                updateLayoutChild(idx, {
                                  ...child,
                                  "on-click-action": {
                                    name: "navigate",
                                    next: { type: "screen", name: e.target.value },
                                  },
                                })
                              }
                              className="mt-0.5 w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white"
                            >
                              <option value="">Select screen...</option>
                              {screens
                                .filter((s) => s.id !== screen.id)
                                .map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.title || s.id}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}
                        <div className="mt-2 border border-gray-200 rounded-lg p-2 bg-white/70">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase">Payload (auto)</span>
                          </div>
                          <pre className="text-[10px] text-gray-700 font-mono whitespace-pre-wrap">
                            {JSON.stringify(autoPayload, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SortableLayoutItem>
            ))}
          </SortableContext>
        </DndContext>
        </div>

        {layoutChildren.length === 0 && (
          <div className="waf-editor-empty">No components yet. Use the buttons above to add content.</div>
        )}
    </div>
  );
};

export default ScreenEditor;
